import type {CourseThemeDocument} from 'course/models/courseThemeDocument';

/**
 * A single color mapping: a color field of {@link CourseThemeDocument} paired
 * with the CSS custom property or variable it should populate.
 */
type ColorVarMapping = readonly [keyof CourseThemeDocument, string];

/**
 * Maps color fields of {@link CourseThemeDocument} to the Bootstrap CSS
 * variables they override (Bootstrap 5.3+ reads these at render time). Setting
 * them retints Bootstrap's own components — body, links, and cards — so a
 * course's color theme reaches Bootstrap markup (e.g. the application's
 * `Card`/`.card` surfaces) without refactoring each one. Only a course that
 * opts into these fields is affected; untouched courses emit nothing.
 */
export const BOOTSTRAP_VAR_MAP: readonly ColorVarMapping[] = [
  ['bodyBackgroundColor', '--bs-body-bg'],
  ['bodyTextColor', '--bs-body-color'],
  ['linkColor', '--bs-link-color'],
  // Bridge surfaces to Bootstrap cards so `Card`/`.card` follow the theme.
  ['surfaceColor', '--bs-card-bg'],
  ['surfaceTextColor', '--bs-card-color'],
];

/**
 * Maps color fields of {@link CourseThemeDocument} to the generic CSS custom
 * properties the app exposes for theming. Components (styled-components,
 * inline styles, SCSS) read these via `var(--course-…, <fallback>)`, so a
 * single theme value can retint markup across every styling system without
 * coupling to Bootstrap. A field left unset in the theme emits no variable, so
 * each consumer's fallback (its pre-theme value) applies unchanged.
 */
export const COURSE_COLOR_VAR_MAP: readonly ColorVarMapping[] = [
  ['bodyBackgroundColor', '--course-bg'],
  ['bodyTextColor', '--course-fg'],
  ['surfaceColor', '--course-surface'],
  ['surfaceTextColor', '--course-surface-fg'],
  ['linkColor', '--course-link'],
];

/**
 * Builds the CSS text that applies a course's color theme: a `:root` block
 * declaring the generic `--course-*` custom properties and the Bootstrap
 * bridge variables, plus a `body` rule so the page background and text color
 * follow the theme. Only fields present (non-empty) on `theme` emit rules, so
 * an untouched course produces an empty string and renders exactly as before.
 *
 * @param theme - The resolved course theme (color fields are read; other
 *     fields are ignored). Fields that are absent or empty strings are skipped.
 * @return A CSS string suitable for injection into a `<style>` element. Returns
 *     an empty string when no color fields are set.
 * @throws Never.
 */
export function buildThemeColorStyles(
  theme: Partial<CourseThemeDocument>,
): string {
  const rootDeclarations: string[] = [];

  for (const [field, cssVar] of [...COURSE_COLOR_VAR_MAP, ...BOOTSTRAP_VAR_MAP]) {
    const value = theme[field];
    if (typeof value === 'string' && value) {
      rootDeclarations.push(`${cssVar}: ${value};`);
    }
  }

  if (rootDeclarations.length === 0) {
    return '';
  }

  // Point body at the custom properties (rather than the raw values) so the
  // whole page and any `var(--course-*)` consumer resolve to one source.
  const bodyDeclarations: string[] = [];
  if (theme.bodyBackgroundColor) {
    bodyDeclarations.push('background-color: var(--course-bg);');
  }
  if (theme.bodyTextColor) {
    bodyDeclarations.push('color: var(--course-fg);');
  }

  const bodyRule =
    bodyDeclarations.length > 0
      ? `\nbody {\n  ${bodyDeclarations.join('\n  ')}\n}`
      : '';

  return `:root {\n  ${rootDeclarations.join('\n  ')}\n}${bodyRule}`;
}
